import { Box, Chip, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { CHECK_LABELS, Checks, failedChecks, wasChecked } from '../../quality/qualityApi';
import { GlowAlert } from '../../shared/components/GlowAlert';
import { errorMessage, setReplayVerdict, Verdict } from '../replaysApi';

const OPTIONS: { value: Verdict; label: string; hint: string }[] = [
    { value: 'better', label: 'Better', hint: 'The replay is better than the original reply' },
    { value: 'same', label: 'Same', hint: 'About as good as the original reply' },
    { value: 'worse', label: 'Worse', hint: 'The replay is worse than the original reply' },
];

interface VerdictControlProps {
    run: { id: string; verdict: Verdict | null; verdict_note: string | null };
    /** Called with the saved verdict and note, so the parent can refresh its own copy. */
    onSaved: (verdict: Verdict | null, note: string | null) => void;
    /** Buttons only, for a table row. */
    compact?: boolean;
}

/** Better / same / worse against the original reply, with an optional one-line reason. */
export const VerdictControl: React.FC<VerdictControlProps> = ({ run, onSaved, compact = false }) => {
    const [verdict, setVerdict] = useState<Verdict | null>(run.verdict);
    const [note, setNote] = useState(run.verdict_note ?? '');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setVerdict(run.verdict);
        setNote(run.verdict_note ?? '');
    }, [run.id, run.verdict, run.verdict_note]);

    const save = async (nextVerdict: Verdict | null, nextNote: string) => {
        setError(null);
        try {
            const saved = await setReplayVerdict(run.id, nextVerdict, nextNote);
            setVerdict(saved.verdict);
            setNote(saved.verdict_note ?? '');
            onSaved(saved.verdict, saved.verdict_note);
        } catch (err) {
            setError(errorMessage(err, 'Failed to save the verdict'));
        }
    };

    return (
        <Box onClick={(event) => event.stopPropagation()} data-testid="verdict-control">
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                {!compact && (
                    <Typography variant="caption" color="text.secondary">
                        Compared with the original:
                    </Typography>
                )}
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={verdict}
                    aria-label="Verdict on this replay"
                    onChange={(_, value: Verdict | null) => save(value, value ? note : '')}
                >
                    {OPTIONS.map((option) => (
                        <Tooltip key={option.value} title={option.hint}>
                            <ToggleButton value={option.value} aria-label={option.label} sx={{ textTransform: 'none', py: 0.25, px: 1 }}>
                                {option.label}
                            </ToggleButton>
                        </Tooltip>
                    ))}
                </ToggleButtonGroup>
            </Box>
            {!compact && verdict && (
                <TextField
                    size="small"
                    fullWidth
                    label="Why (optional)"
                    value={note}
                    inputProps={{ maxLength: 500 }}
                    onChange={(event) => setNote(event.target.value)}
                    onBlur={() => note.trim() !== (run.verdict_note ?? '') && save(verdict, note)}
                    sx={{ mt: 1 }}
                />
            )}
            {error && (
                <GlowAlert sx={{ mt: 1 }}>
                    {error}
                </GlowAlert>
            )}
        </Box>
    );
};

/** The automatic checks of one reply: what failed, or that it passed every one it could judge. */
export const CheckChips: React.FC<{ checks: Checks | null }> = ({ checks }) => {
    if (!wasChecked(checks)) return null;
    const failed = failedChecks(checks);
    return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }} data-testid="check-chips">
            {failed.length === 0 && <Chip size="small" variant="outlined" color="success" label="Passed the automatic checks" />}
            {failed.map((name) => (
                <Chip key={name} size="small" color="warning" label={CHECK_LABELS[name]} />
            ))}
        </Box>
    );
};
